import React, { useEffect, useRef, useState } from "react";
import { Grid, Button } from "@material-ui/core";
import * as _ from "lodash";
import Loader from "../../../../../common/dialogs/loader/loader";
import ModalFlyout from "../../../../ui_utilities/modalFlyout";
import ChartComponent from "./components/ChartComponent";
import ChartOrderModal from "./components/ChartOrderModal";
import ChartControlPanel from "./components/ChartControlPanel";
import { buildGraphListArray } from "./utils/chartSeriesBuilder";
import { downloadChartAsPPT } from "./utils/chartExport";
import {
  CHART_DIMENSIONS,
  SPECIAL_REPORT_TABS,
} from "./constants/chartConstants";

const ChartAndGraphs = (props) => {
  // State management
  const [loaderstatus, setLoaderstatus] = useState(false);
  const [graphList, setGraphlist] = useState([]);
  const [selectedGraph, setSelectedGraph] = useState([]);
  const [showpreview, setShowpreview] = useState(false);
  const [previewchartid, setPreviewchartid] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [graphTemplate, setGraphTemplate] = useState(props.graphTemplate);
  const [removecounter, setRemovecounter] = useState(0);

  // Chart refs for export functionality
  const chartRefs = {
    ecm: useRef(null),
    milkprod: useRef(null),
    lact1: useRef(null),
    lact2: useRef(null),
    lact3: useRef(null),
    herddata: useRef(null),
    calfheifer: useRef(null),
    daysopen: useRef(null),
    percentfat: useRef(null),
    somaticcell: useRef(null),
    percentprotein: useRef(null),
    totallbs: useRef(null),
    avgdim: useRef(null),
    calvinginterval: useRef(null),
    annualcull: useRef(null),
    lactcurve: useRef(null),
  };

  const useavgdailymilkflag = props.subherddata.use_avg_daily_milk || false;

  // Sync graph template with props
  useEffect(() => {
    setGraphTemplate(props.graphTemplate);
  }, [props.graphTemplate]);

  // Initialize graph data
  useEffect(() => {
    if (removecounter === 0) {
      // Update milk chart based on flag
      if (useavgdailymilkflag) {
        props.selectedGraph[1] = {
          key: 2,
          id: "weeklymilkchart",
          name: "Weekly Milk",
          value: true,
          width: "half_page",
        };
      } else {
        props.selectedGraph[1] = {
          key: 2,
          id: "testdaymilkchart",
          name: "Test Day Milk",
          value: true,
          width: "half_page",
        };
      }
    }

    // Build graph list from data
    const graphListArray = buildGraphListArray({
      graphResult: props.graphdataresponse,
      curveChart: props.reportResponse.Lacation_graph_values,
      reportTab: props.reporttab,
      useavgdailymilkflag,
      reportResponse: props.reportResponse,
      lactationcurvemaxy: props.lactationcurvemaxy,
      lactationcurveminy: props.lactationcurveminy,
      useFullMilkWeeks: props.useFullMilkWeeks,
      showFullMilkWeeksToggle: props.showFullMilkWeeksToggle,
    });

    setGraphlist(graphListArray);

    if (props.selectedGraph.length > 0) {
      if (SPECIAL_REPORT_TABS.includes(props.reporttab)) {
        const filtered = graphTemplate.filter(
          (item) => item.id !== "lactationcurvechart",
        );
        setSelectedGraph(filtered);
      } else {
        setSelectedGraph(props.graphTemplate);
      }
    }

    setLoaderstatus(false);
    setRemovecounter(1);
  }, [props]);

  // Chart rendering helper
  const renderChart = (chartData, index, isPreview = false) => {
    if (!chartData || graphList.length === 0) return null;

    const chart = graphList[index];
    if (!chart) return null;

    const chartId = isPreview ? `${chart.id}preview` : chart.id;
    const options = isPreview ? _.cloneDeep(chart.option) : chart.option;

    if (isPreview && options) {
      options.chart.id = chartId;
    }

    const refKey = Object.keys(chartRefs)[index];
    const chartRef = chartRefs[refKey];

    return (
      <ChartComponent
        chartRef={chartRef}
        title={chart.name}
        chartId={chartId}
        options={options}
        series={chart.series}
        showDownloadButton={isPreview}
        onDownload={() => handleDownloadChart(chartRef, chart.name)}
        chartName={chart.name}
      />
    );
  };

  // Export handler
  const handleDownloadChart = async (chartRef, chartName) => {
    await downloadChartAsPPT(
      chartRef,
      chartName,
      props.dairyname,
      props.subherddata,
    );
  };

  // Modal handlers
  const handleToggleModal = () => {
    setOpenModal(!openModal);
  };

  const handleEnableChart = (chartId, checked) => {
    props.setGraphTemplate((prevTemplate) =>
      prevTemplate.map((item) =>
        item.id === chartId ? { ...item, value: checked } : item,
      ),
    );
  };

  const handleEnableChartModal = (chartId, checked) => {
    setGraphTemplate((prevTemplate) =>
      prevTemplate.map((item) =>
        item.id === chartId ? { ...item, value: checked } : item,
      ),
    );
  };

  const handleWidthChange = (event, index) => {
    const newWidth = event.target.value;

    props.setGraphTemplate((prevTemplate) =>
      prevTemplate.map((item, idx) =>
        idx === index ? { ...item, width: newWidth } : item,
      ),
    );
  };

  const handlePreviewClick = (chartId) => {
    setPreviewchartid(chartId);
    setShowpreview(true);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(graphTemplate);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    setGraphTemplate(reorderedItems);
  };

  const updateChartOrder = () => {
    props.setGraphTemplate(graphTemplate);
    setOpenModal(false);
  };

  // Render hidden charts for export
  const renderHiddenCharts = () => {
    return graphList.map((chart, idx) => (
      <div key={idx} style={{ width: `${CHART_DIMENSIONS.EXPORT_WIDTH}px` }}>
        {renderChart(chart, idx, false)}
      </div>
    ));
  };

  // Render visible charts based on template
  const renderVisibleCharts = () => {
    let chartsToRender = graphTemplate;

    if (SPECIAL_REPORT_TABS.includes(props.reporttab)) {
      chartsToRender = graphTemplate.filter(
        (item) => item.id !== "lactationcurvechart",
      );
    }

    return chartsToRender.map((graph, idx) => {
      const chartIndex = graphList.findIndex((item) => item.id === graph.id);
      if (chartIndex === -1) return null;

      return (
        <React.Fragment key={idx}>
          {/* Chart Control Panel */}
          <ChartControlPanel
            chartNumber={idx + 1}
            chartName={graph.name}
            chartId={graph.id}
            chartValue={graph.value}
            chartWidth={graph.width}
            onEnableChange={handleEnableChart}
            onWidthChange={(event) => handleWidthChange(event, idx)}
            onPreviewClick={() => handlePreviewClick(graph.id)}
          />
        </React.Fragment>
      );
    });
  };

  // Render preview chart in modal
  const renderPreviewChart = () => {
    const chartIndex = graphList.findIndex(
      (item) => item.id === previewchartid,
    );
    if (chartIndex === -1) return null;

    return renderChart(graphList[chartIndex], chartIndex, true);
  };

  return (
    <div className="graphWrapper">
      <Grid item xs={12} style={{ textAlign: "end" }}>
        <Button
          variant="contained"
          className="button grey size-mini"
          type="button"
          size="small"
          onClick={handleToggleModal}
        >
          Drag and Drop Order
        </Button>
      </Grid>

      {/* Hidden charts for export */}
      <div className="hidechartfromview">{renderHiddenCharts()}</div>

      {/* Visible charts */}
      {loaderstatus ? (
        <Loader radius={50} type="large" />
      ) : (
        <>
          {selectedGraph.length === 0 ? (
            <Grid item xs={12}>
              <p>No charts selected</p>
            </Grid>
          ) : (
            renderVisibleCharts()
          )}
        </>
      )}

      {/* Preview Modal */}
      <ModalFlyout
        state={showpreview}
        setstate={setShowpreview}
        width="xl"
        id={props.id}
      >
        <div>{renderPreviewChart()}</div>
      </ModalFlyout>

      {/* Chart Order Modal */}
      <ChartOrderModal
        open={openModal}
        onClose={handleToggleModal}
        graphTemplate={graphTemplate}
        reportTab={props.reporttab}
        onDragEnd={onDragEnd}
        onToggleChart={handleEnableChartModal}
        onUpdateOrder={updateChartOrder}
      />
    </div>
  );
};

export default ChartAndGraphs;
